/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Tag, Plus, X, Check, Loader2, Settings2 } from "lucide-react";

const PALETTE = ["#0ea5e9", "#f97316", "#8b5cf6", "#10b981", "#e11d48", "#f59e0b", "#06b6d4", "#84cc16", "#a855f7", "#64748b"];
const hashColor = (t) => PALETTE[[...String(t)].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7) % PALETTE.length];

/* 标签目录：预设 + 已使用；任何 CRM 页面共用 */
export function useTagCatalog(api) {
  const [cat, setCat] = useState({ presets: [], inUse: [], canEditPresets: false });
  const reload = () => api.get("/crm/tags").then(({ data }) => setCat(data)).catch(() => {});
  useEffect(() => { reload(); }, [api]); // eslint-disable-line
  const all = useMemo(() => { const s = new Set(cat.presets); cat.inUse.forEach((x) => s.add(x.tag)); return [...s]; }, [cat]);
  const count = useMemo(() => Object.fromEntries(cat.inUse.map((x) => [x.tag, x.count])), [cat]);
  return { ...cat, all, count, reload, setCat };
}

export function TagChip({ tag, onRemove, small, active, onClick, count, testId }) {
  const c = hashColor(tag);
  return (
    <span data-testid={testId} onClick={onClick} role={onClick ? "button" : undefined}
      className={`inline-flex items-center gap-1 rounded-full font-semibold whitespace-nowrap ${small ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"} ${onClick ? "cursor-pointer hover:brightness-95" : ""} ${active ? "ring-2 ring-offset-1" : ""}`}
      style={{ background: `${c}1f`, color: c, "--tw-ring-color": c }}>
      {tag}{count != null && <b className="opacity-70">{count}</b>}
      {onRemove && <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(tag); }} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 hover:bg-white/60 rounded-full" aria-label="remove"><X className="h-3 w-3" /></button>}
    </span>
  );
}

export function TagChips({ tags = [], max = 3, small = true, testId }) {
  if (!tags.length) return null;
  const rest = tags.length - max;
  return (
    <span className="inline-flex flex-wrap gap-1 items-center" data-testid={testId}>
      {tags.slice(0, max).map((t) => <TagChip key={t} tag={t} small={small} />)}
      {rest > 0 && <span className="text-[10px] text-slate-400" title={tags.slice(max).join("、")}>+{rest}</span>}
    </span>
  );
}

/* 勾选 / 输入标签的公共面板 */
function TagPicker({ catalog, value, onChange, zh, count }) {
  const [txt, setTxt] = useState("");
  const add = (t) => { t = (t || "").trim().slice(0, 30); if (!t) return; if (!value.some((x) => x.toLowerCase() === t.toLowerCase())) onChange([...value, t]); setTxt(""); };
  const options = catalog.filter((t) => !txt || t.toLowerCase().includes(txt.toLowerCase()));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 min-h-[24px]" data-testid="tag-picker-selected">
        {value.length === 0 && <span className="text-[11px] text-slate-400">{zh ? "尚未选择标签" : "No tags selected"}</span>}
        {value.map((t) => <TagChip key={t} tag={t} onRemove={(x) => onChange(value.filter((v) => v !== x))} />)}
      </div>
      <div className="flex-wrap md:flex-nowrap flex gap-1">
        <input data-testid="tag-picker-input" value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(txt); } }}
          placeholder={zh ? "搜索或输入新标签，回车添加" : "Search or type a new tag, Enter to add"} className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5" />
        <button type="button" data-testid="tag-picker-add" onClick={() => add(txt)} disabled={!txt.trim()} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs px-2 rounded-md border border-slate-200 text-slate-600 disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex flex-wrap gap-1 max-h-40 overflow-auto" data-testid="tag-picker-options">
        {options.map((t) => { const on = value.some((v) => v.toLowerCase() === t.toLowerCase()); return <TagChip key={t} tag={t} active={on} count={count?.[t]} onClick={() => (on ? onChange(value.filter((v) => v.toLowerCase() !== t.toLowerCase())) : add(t))} testId={`tag-opt-${t}`} />; })}
        {options.length === 0 && <span className="text-[11px] text-slate-400">{zh ? "无匹配，回车新建" : "No match — press Enter to create"}</span>}
      </div>
    </div>
  );
}

/* 单个客户的标签编辑（Popover）：显示芯片 + 编辑按钮 */
export function TagEditor({ api, lead, catalog, zh, onSaved, max = 3, compact }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(lead.tags || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(lead.tags || []); }, [lead.tags]);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/crm/leads/${lead.id}/tags`, { tags: val }); toast.success(zh ? "标签已保存" : "Tags saved"); onSaved?.(data.tags); catalog.reload?.(); setOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
    setSaving(false);
  };
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <TagChips tags={lead.tags} max={max} testId={`lead-tags-${lead.id}`} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`lead-tags-edit-${lead.id}`} title={zh ? "编辑标签" : "Edit tags"} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`inline-flex items-center gap-0.5 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-slate-700 hover:border-slate-400 ${compact ? "px-1 py-0.5" : "px-1.5 py-0.5 text-[10px]"}`)}>
            <Tag className="h-3 w-3" />{!compact && !(lead.tags || []).length && (zh ? "打标签" : "Tag")}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3" data-testid={`lead-tags-popover-${lead.id}`}>
          <p className="text-xs font-semibold text-slate-700 mb-2 inline-flex items-center gap-1"><Tag className="h-3.5 w-3.5 text-violet-500" />{zh ? "客户标签" : "Customer tags"} · {lead.clientName}</p>
          <TagPicker catalog={catalog.all} value={val} onChange={setVal} zh={zh} count={catalog.count} />
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)} data-testid="lead-tags-cancel">{zh ? "取消" : "Cancel"}</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="lead-tags-save" className="bg-violet-600 hover:bg-violet-700 text-white">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}{zh ? "保存" : "Save"}</Button>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}

/* 列表筛选：多选标签 + 任一 / 全部 */
export function TagFilter({ catalog, value = [], onChange, mode = "any", onMode, zh, testId = "tag-filter" }) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const opts = catalog.all.filter((t) => !txt || t.toLowerCase().includes(txt.toLowerCase()));
  const toggle = (t) => onChange(value.includes(t) ? value.filter((v) => v !== t) : [...value, t]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`inline-flex items-center gap-1.5 text-sm rounded-lg border px-2.5 py-1.5 ${value.length ? "border-violet-300 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600 bg-white"}`)}>
          <Tag className="h-3.5 w-3.5" />{zh ? "标签" : "Tags"}{value.length > 0 && <b className="text-xs">{value.length}</b>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3" data-testid={`${testId}-popover`}>
        <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-700">{zh ? "按标签筛选" : "Filter by tags"}</p>
          {onMode && <div className="inline-flex rounded-md bg-slate-100 p-0.5 text-[10px] font-semibold">{[["any", zh ? "任一" : "Any"], ["all", zh ? "全部" : "All"]].map(([k, l]) => <button key={k} type="button" data-testid={`${testId}-mode-${k}`} onClick={() => onMode(k)} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2 py-0.5 rounded ${mode === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`)}>{l}</button>)}</div>}
        </div>
        <input data-testid={`${testId}-search`} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={zh ? "搜索标签" : "Search tags"} className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 mb-2" />
        <div className="flex flex-wrap gap-1 max-h-48 overflow-auto">
          {opts.map((t) => <TagChip key={t} tag={t} active={value.includes(t)} count={catalog.count[t]} onClick={() => toggle(t)} testId={`${testId}-opt-${t}`} />)}
          {opts.length === 0 && <span className="text-[11px] text-slate-400">{zh ? "暂无标签" : "No tags yet"}</span>}
        </div>
        {value.length > 0 && <button type="button" data-testid={`${testId}-clear`} onClick={() => onChange([])} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 mt-2 text-[11px] text-slate-500 hover:text-slate-800 underline">{zh ? "清除标签筛选" : "Clear"}</button>}
      </PopoverContent>
    </Popover>
  );
}

/* 批量打标签 / 移除（经理） */
export function BulkTagDialog({ api, open, onOpenChange, ids, catalog, zh, onDone, filter = null, count = 0 }) {
  const [add, setAdd] = useState([]);
  const [remove, setRemove] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAdd([]); setRemove([]); } }, [open]);
  const n = filter ? count : ids.length;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/crm/leads/bulk", { action: "tags", ...(filter ? { filter } : { ids }), params: { add, remove } });
      const updated = data.done;
      toast.success(zh ? `已更新 ${updated} 个客户的标签` : `Updated ${updated} leads`); catalog.reload?.(); onDone?.(); onOpenChange(false);
    }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-tag-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="h-4 w-4 text-violet-600" />{zh ? `批量打标签 · ${n} 个客户${filter ? "（当前筛选全部）" : ""}` : `Bulk tags · ${n} leads${filter ? " (all matching)" : ""}`}</DialogTitle><DialogDescription>{zh ? "添加的标签会追加到所选客户（已有的不重复）；移除只影响已带该标签的客户。" : "Added tags are appended; removal only affects leads carrying the tag."}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><p className="text-xs font-semibold text-emerald-700 mb-1">{zh ? "添加标签" : "Add tags"}</p><TagPicker catalog={catalog.all} value={add} onChange={setAdd} zh={zh} count={catalog.count} /></div>
          <div><p className="text-xs font-semibold text-rose-700 mb-1">{zh ? "移除标签" : "Remove tags"}</p><TagPicker catalog={catalog.all} value={remove} onChange={setRemove} zh={zh} count={catalog.count} /></div>
        </div>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-tag-cancel">{zh ? "取消" : "Cancel"}</Button>
          <Button onClick={run} disabled={busy || (!add.length && !remove.length)} data-testid="bulk-tag-confirm" className="bg-violet-600 hover:bg-violet-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (zh ? "应用" : "Apply")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* 经理维护预设标签 */
export function TagPresetsDialog({ api, open, onOpenChange, catalog, zh }) {
  const [val, setVal] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setVal(catalog.presets || []); }, [open, catalog.presets]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/crm/tags/presets", { presets: val }); toast.success(zh ? "预设标签已保存" : "Presets saved"); catalog.reload?.(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tag-presets-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="h-4 w-4 text-slate-600" />{zh ? "预设标签（全公司）" : "Preset tags (company-wide)"}</DialogTitle><DialogDescription>{zh ? "预设标签会出现在所有打标签面板顶部；已在客户上使用的标签即使移出预设也会保留。" : "Presets appear in every tag picker; tags already on leads are kept."}</DialogDescription></DialogHeader>
        <TagPicker catalog={catalog.all} value={val} onChange={setVal} zh={zh} count={catalog.count} />
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tag-presets-cancel">{zh ? "取消" : "Cancel"}</Button>
          <Button onClick={save} disabled={busy} data-testid="tag-presets-save" className="bg-slate-900 hover:bg-slate-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (zh ? "保存" : "Save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
